import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { timeAgo } from "@/lib/api";
import { Article } from "@/lib/types";
import { useArticlePath } from "@/lib/i18n";
import { SECTOR_LABELS } from "@/components/layernav/navConfig";
import { trackEvent } from "@/hooks/useArticleAnalytics";

const catLabel = (c?: string) =>
  c ? SECTOR_LABELS[c] || c.replace(/-/g, " ").replace(/\b\w/g, (m) => m.toUpperCase()) : null;

/* Right-rail module 3 (§27): semantically related stories — hides when empty. */
export const RelatedCoveragePanel = ({ items, category }: { items: Article[]; category?: string }) => {
  const toArticle = useArticlePath();
  if (!items.length) return null;
  return (
    <section data-testid="related-coverage-panel" data-track="related_coverage"
      className="rounded-xl border border-white/[0.08] bg-[#0a0b10] p-4">
      <p className="font-mono text-[9px] font-bold uppercase tracking-[0.28em] text-[#00E5FF]">Related Coverage</p>
      <div className="mt-3.5 space-y-3.5">
        {items.slice(0, 3).map((a, i) => (
          <Link key={a.slug} to={toArticle(a.slug)} data-testid={`related-coverage-${i}`}
            onClick={() => trackEvent("related_intelligence_click", { slug: a.slug })}
            className="group flex items-start gap-3">
            {a.hero_image_url && (
              <img src={a.hero_image_url} alt="" loading="lazy" width={64} height={48}
                className="h-12 w-16 shrink-0 rounded-md object-cover border border-white/10" />
            )}
            <span className="min-w-0">
              {a.categories?.[0] && (
                <span className="block font-mono text-[8px] font-bold uppercase tracking-widest text-[#A78BFA]">
                  {catLabel(a.categories[0])}
                </span>
              )}
              <span className="mt-0.5 block text-[13px] font-semibold text-gray-200 group-hover:text-white leading-snug line-clamp-2 transition-colors">
                {a.title}
              </span>
              <span className="mt-1 block font-mono text-[9.5px] text-gray-600">
                {timeAgo(a.published_at)}{a.read_minutes ? ` · ${a.read_minutes} min read` : ""}
              </span>
            </span>
          </Link>
        ))}
      </div>
      {category && (
        <Link to={`/${category}`} data-testid="related-coverage-view-all"
          className="mt-4 pt-3 border-t border-white/[0.06] flex items-center gap-1.5 font-mono text-[10.5px] font-bold uppercase tracking-widest text-[#00E5FF] hover:text-white transition-colors">
          View all related <ArrowRight className="h-3 w-3" />
        </Link>
      )}
    </section>
  );
};
